import { useEffect, useState } from 'react';
import { Scrollbars } from 'react-custom-scrollbars-2';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import { IMailerPayload } from 'models/mailer-payload.interface';

import { ERROR_MESSAGES } from 'constants/error-messages.constant';

import { addErrorMessageAction } from 'actions/global-actions';

import { mailerService } from 'services/mailer.service';

import ContactForm from './components/contactForm/ContactForm';
import ContactOffice from './components/contactOffice/ContactOffice';
import ContactPerson from './components/contactPerson/ContactPerson';
import Page from 'components/page/Page';
import PageWrapper from 'components/pageWrapper/PageWrapper';
import VideoBanner from 'components/videoBanner/VideoBanner';

import BannerMp4 from 'assets/contact/banner.mp4';
import BannerWebm from 'assets/contact/banner.webm';

import styles from './ContactRoute.module.scss';

const ContactRoute = () => {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const { t } = useTranslation('notifications');
  const successMessageFadeOutDelay = 3000;

  useEffect(() => {
    if (!isSuccess) {
      return;
    }

    const timer = setTimeout(() => {
      setIsSuccess(false);
    }, successMessageFadeOutDelay);

    return () => {
      clearTimeout(timer);
    };
  }, [isSuccess]);

  const onSubmit = async (payload: IMailerPayload, resetForm: () => void) => {
    setIsLoading(true);

    try {
      await mailerService(payload);
      setIsSuccess(true);
      resetForm();
    } catch (error) {
      setIsSuccess(false);
      dispatch(addErrorMessageAction(t(ERROR_MESSAGES.errorCouldNotSendEmail)));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Page pageBackground="dark">
      <Scrollbars style={{ width: '100%', height: '100%' }}>
        <PageWrapper landingFooter homeFooter={false}>
          <div className={styles['banner']}>
            <VideoBanner
              className={styles['banner-video']}
              webMVideoUrl={BannerWebm}
              mp4VideoUrl={BannerMp4}
              videoAnimationThreshold={0}
            />
            <div className="tw-z-4 tw-relative tw-flex tw-flex-col tw-py-[116px] tw-text-center sm:tw-py-[189px] sm:tw-pb-[199px]">
              <h1 className="tw-m-0 tw-text-center tw-text-[52px] tw-uppercase tw-text-white md:tw-text-[68px] xl:tw-text-8xl">
                Get in touch with us
              </h1>
            </div>
          </div>
          <div className="tw-mx-auto tw-block tw-max-w-[1320px] tw-justify-around tw-px-5 tw-pb-[50px] sm:tw-pt-[60px] lg:tw-pb-[80px] xl:tw-flex xl:tw-items-start xl:tw-gap-8">
            <div className="tw-flex-col">
              <ContactPerson />
              <div className="tw-hidden xl:tw-block">
                <ContactOffice />
              </div>
            </div>
            <ContactForm submit={onSubmit} isSuccess={isSuccess} isLoading={isLoading} />
          </div>
          <div className="tw-bg-darkBlue tw-px-5 tw-py-[50px] sm:tw-py-15 lg:tw-py-24 xl:tw-hidden">
            <ContactOffice />
          </div>
        </PageWrapper>
      </Scrollbars>
    </Page>
  );
};

export default ContactRoute;
